import { GlobalStore, LogBaseService } from '@roc/feature-app-core';
import { action, computed, flow, makeObservable, observable } from 'mobx';
import { getUniqueId } from '@roc/feature-utils';
import { InsuranceInformationBaseStore } from './insuranceInformationBaseStore';
import { FloodZoneStore } from './floodZoneStore';
import { QUOTE_STATUS } from '../utils';
import {
  CoverageType,
  INSURANCE_REVIEW_EVENT_TYPE_V2,
  InsuranceAutomationStatus,
  AutomationEventTypes,
} from '../util';

export abstract class InsuranceInformationReviewBaseStore {
  globalStore: GlobalStore;
  insuranceInformationStore: InsuranceInformationBaseStore;
  logService: LogBaseService;
  floodZoneStore: FloodZoneStore;
  insuranceReviewData: any;
  quotes: any[];
  quotesOptions: any[];
  selectedQuote: string;
  insuranceQuoteDataId: number;
  eventList: Map<string, any>;
  insuranceAutomationStatus: string;
  automatedSectionFlags: any;
  previewSectionFlags: any;
  showPreview: boolean;
  sectionData: any;
  automatedDocumentsData: any;
  readOnlyPackageSection: any;
  sectionDataAutomated: any;
  previewDataReplacementList: any[];
  clearFlagsEventObject: any;
  manualSendForRevisionsEventObject: any;

  abstract getInsuranceQuotes(loanId);
  abstract getInsuranceReviewData(quoteId: string);
  abstract saveData();
  abstract saveClearFlagButtonLogInfo();
  abstract createResolutions(data: any);
  abstract getRevisionText(request: any);
  abstract changeQuoteStatus(quoteId: any, status: string);
  abstract sendPreviewDetails(action: string, quoteId: any);
  abstract updateAutoRevisionForQuote(quoteId, value);
  abstract getInsuranceQuotesAndSetSelected(loanId);

  constructor(globalStore, insuranceInformationStore) {
    this.globalStore = globalStore;
    this.insuranceInformationStore = insuranceInformationStore;
    this.floodZoneStore = new FloodZoneStore(globalStore);

    this.setDefaults();
    makeObservable(this, {
      quotes: observable,
      quotesOptions: observable,
      insuranceReviewData: observable,
      selectedQuote: observable,
      getInsuranceReviewData: flow,
      setSelectedQuote: action,
      handleItemChange: action,
      getInsuranceQuotes: flow,
      saveData: flow,
      saveClearFlagButtonLogInfo: flow,
      numberOfResolutionsSelected: computed,
      selectedQuotesData: computed,
      setReviewComplete: flow,
      requestResolutions: flow,
      createResolutions: flow,
      changeQuoteStatus: flow,
      availableReviewSections: computed,
      setQuoteDeclined: flow,
      addEvent: action,
      addClearFlagButtonEvent: action,
      sectionsMarkedForRevision: computed,
      getRevisionDocumentIds: action,
      getRevisionItemsForCoverage: action,
      setDefaults: action,
      setRevisionComment: flow,
      getRevisionText: flow,
      insuranceAutomationStatus: observable,
      readOnlyForm: computed,
      automationStatusMappingValue: computed,
      showPreview: observable,
      acceptPreview: flow,
      declinePreview: flow,
      sendPreviewDetails: flow,
      setPreviewItemValue: action,
      previewDataReplacementList: observable,
      getItemByUniqueIdentifier: observable,
      sectionData: observable,
      sectionDataAutomated: observable,
      removeFlagForDoc: flow,
      updateAutoRevisionForQuote: flow,
      updateAutomatedResolutionForQuote: flow,
      getInsuranceQuotesAndSetSelected: flow,
    });
  }

  setDefaults() {
    this.insuranceReviewData = {};
    this.quotes = [];
    this.quotesOptions = [];
    this.selectedQuote = null;
    this.eventList = new Map();
    this.clearFlagsEventObject = {};
    this.manualSendForRevisionsEventObject = {};
  }

  setSelectedQuote(value) {
    this.selectedQuote = value;
  }

  normalizeAutomatedReviewItems(data) {
    Object.keys(data).forEach(key => {
      data[key] = data[key]
        ?.map(section => ({
          ...section,
          items: section.items?.map(item => ({
            ...item,
            uniqueId: getUniqueId(),
          })),
        }))
        .sort((a, b) => a.sectionName?.localeCompare(b.sectionName));
    });
    return data;
  }

  addEvent({
    coverage,
    mapping,
    sectionName,
    itemDisplayName,
    value,
  }: {
    coverage: string;
    mapping: string;
    sectionName: string;
    itemDisplayName: string;
    value?: any;
  }) {
    switch (mapping) {
      case 'sendForRevision':
        const flagAction = value
          ? AutomationEventTypes.ITEM_REVISION_FLAGGED_EVENT
          : AutomationEventTypes.ITEM_REVISION_UNFLAGGED_EVENT;

        this.eventList.set(
          `FLAG-${coverage}-${sectionName}-${itemDisplayName}`,
          {
            reviewType: INSURANCE_REVIEW_EVENT_TYPE_V2,
            actionPerformed: flagAction,
            sectionName: sectionName,
            itemName: itemDisplayName,
            coverage: coverage,
            value: value,
          }
        );
        break;
      case 'needsRevisionFlag':
        const revisionAction = value
          ? AutomationEventTypes.ITEM_FLAGGED_EVENT
          : AutomationEventTypes.ITEM_UNFLAGGED_EVENT;

        this.eventList.set(
          `REVISION-${coverage}-${sectionName}-${itemDisplayName}`,
          {
            reviewType: INSURANCE_REVIEW_EVENT_TYPE_V2,
            actionPerformed: revisionAction,
            sectionName: sectionName,
            itemName: itemDisplayName,
            coverage: coverage,
            value: value,
          }
        );
        break;
      case 'data':
        this.eventList.set(
          `${AutomationEventTypes.ITEM_DATA_UPDATED}-${coverage}-${sectionName}-${itemDisplayName}`,
          {
            reviewType: INSURANCE_REVIEW_EVENT_TYPE_V2,
            actionPerformed: AutomationEventTypes.ITEM_DATA_UPDATED,
            sectionName: sectionName,
            itemName: itemDisplayName,
            coverage: coverage,
            value: value,
          }
        );
    }
  }

  addClearFlagButtonEvent(sectionKey, selectedQuote) {
    this.clearFlagsEventObject = {
      actionPerformed: AutomationEventTypes.ITEM_CLEAR_FLAGS,
      itemName: '',
      reviewType: INSURANCE_REVIEW_EVENT_TYPE_V2,
      sectionName: sectionKey,
      value: null,
      insuranceQuoteId: selectedQuote,
    };
  }

  addManualSendForRevisionsEvent(): void {
    const sectionName = this.sectionsMarkedForRevision.length > 0
      ? this.sectionsMarkedForRevision[0]
      : 'Package Coverage';

    const selectedQuote = this.getSelectedQuote() ?? this.insuranceInformationStore.quotes[0];

    this.manualSendForRevisionsEventObject = {
      actionPerformed: AutomationEventTypes.ITEMS_SENT_FOR_RESOLUTIONS,
      itemName: '',
      reviewType: INSURANCE_REVIEW_EVENT_TYPE_V2,
      sectionName: sectionName,
      value: null,
      insuranceQuoteId: selectedQuote?.insuranceQuoteDetailId,
      loanId: this.insuranceInformationStore.loanId
    }
  }

  handleItemChange(coverage, mapping, uniqueName, value) {
    this.insuranceReviewData[coverage] = this.insuranceReviewData[coverage].map(
      section => ({
        ...section,
        items: section.items.map(item =>
          item.uniqueName === uniqueName
            ? {
              ...item,
              [mapping]: value,
            }
            : item
        ),
      })
    );
  }

  get numberOfResolutionsSelected() {
    let counter = 0;
    Object.keys(this.insuranceReviewData).forEach(key => {
      this.insuranceReviewData[key].map(section =>
        section.items
          .filter(item => item.sendForRevision)
          .reduce((acc, item) => (counter += 1), 0)
      );
    });
    return counter;
  }

  get selectedQuotesData() {
    return this.quotes.filter(
      q => q.insuranceQuoteDetailId == this.selectedQuote
    );
  }

  getInsuranceReviewValue(
    coverageType: string,
    sectionName: string,
    itemName: string
  ) {
    return this.insuranceReviewData[coverageType]
      .find(section => section?.sectionName == sectionName)
      ?.items?.find(item => item?.itemDisplayName == itemName)?.data;
  }

  getInsuranceReviewerComment(coverageType: string, uniqueName: string) {
    return this.insuranceReviewData[coverageType]
      ?.map(section => section?.items)
      ?.reduce((acc, items) => acc.concat(items), [])
      .find(item => item?.uniqueName == uniqueName)?.reviewerComment;
  }

  getSelectedQuote() {
    return this.quotes?.find(
      quote => quote.insuranceQuoteDetailId == this.selectedQuote
    );
  }

  *setReviewComplete() {
    const selectedQuote = this.getSelectedQuote();
    //this.fillOutQuoteWithReviewData(selectedQuote);
    this.insuranceInformationStore.insuranceQuoteFormStore.loadForm(
      selectedQuote
    );
    if (this.insuranceInformationStore.validateQuote(selectedQuote)) {
      yield this.saveData();
      selectedQuote.status = QUOTE_STATUS.APPROVED;
      yield this.insuranceInformationStore.saveInsuranceQuoteDetails(
        selectedQuote
      );
      yield this.getInsuranceQuotes(this.insuranceInformationStore.loanId);
      this.setSelectedQuote(selectedQuote?.insuranceQuoteDetailId);
      this.globalStore.notificationStore.showSuccessNotification({
        message: `${selectedQuote?.quoteShowId} has been approved`,
      });
    }
  }

  *setQuoteDeclined(quote: any) {
    yield this.changeQuoteStatus(
      quote.insuranceQuoteDetailId,
      QUOTE_STATUS.IN_REVIEW
    );
    yield this.insuranceInformationStore.getAvailableQuotesByLoanId();
  }

  getCoverageType(selectedQuote: any) {
    if (selectedQuote?.packageCoverage) {
      return CoverageType.PACKAGE;
    } else if (selectedQuote?.propertyCoverage) {
      return CoverageType.PROPERTY;
    } else if (selectedQuote?.generalLiabilityCoverage) {
      return CoverageType.LIABILITY;
    } else if (selectedQuote?.floodCoverage) {
      return CoverageType.FLOOD;
    }
  }

  setReplacementMap(filteredItems: any) {
    this.previewDataReplacementList = filteredItems;
  }

  setPreviewItemValue(identifier: string, field: string, value: any) {
    this.previewDataReplacementList = this.previewDataReplacementList.map(
      section => ({
        ...section,
        items: section.items.map(item =>
          item.uniqueName == identifier
            ? {
              ...item,
              [field]: value,
            }
            : item
        ),
      })
    );
  }

  getItemByUniqueIdentifier(identifier: string) {
    return this.previewDataReplacementList
      .map(section => section.items)
      .reduce((acc, itemList) => acc.concat(itemList), [])
      .find(item => item.uniqueName == identifier);
  }

  get availableReviewSections() {
    return Object.keys(this.insuranceReviewData);
  }

  get sectionsMarkedForRevision() {
    return Object.keys(this.insuranceReviewData).filter(key =>
      this.insuranceReviewData[key]
        .map(section => section.items)
        .some(itemList => itemList.some(item => item.sendForRevision))
    );
  }

  getRevisionDocumentIds(): number[] {
    if (
      !this.sectionsMarkedForRevision?.length ||
      !this.automatedDocumentsData
    ) {
      return [];
    }

    const normalizedDocs = new Map(
      Object.keys(this.automatedDocumentsData).map(k => [
        this.normalizeKey(k),
        this.automatedDocumentsData[k],
      ])
    );

    return this.sectionsMarkedForRevision
      .map(k => normalizedDocs.get(this.normalizeKey(k))?.documentId)
      .filter((id): id is number => !!id);
  }

  fillOutQuoteWithReviewData(selectedQuote: any) {
    const insuranceReviewCoverageKey = this.getCoverageType(selectedQuote);
    const insuranceAgentName = this.getInsuranceReviewValue(
      insuranceReviewCoverageKey,
      'Agency',
      'Name'
    );
    const insuranceAgentEmail = this.getInsuranceReviewValue(
      insuranceReviewCoverageKey,
      'Agency',
      'Email'
    );
    const insuranceAgentPhone = this.getInsuranceReviewValue(
      insuranceReviewCoverageKey,
      'Agency',
      'Phone'
    );
    const insuranceAgentAddress = this.getInsuranceReviewValue(
      insuranceReviewCoverageKey,
      'Agency',
      'Address'
    );

    const carrier = this.getInsuranceReviewValue(
      insuranceReviewCoverageKey,
      'Carrier',
      'Name'
    );
    const naic = this.getInsuranceReviewValue(
      insuranceReviewCoverageKey,
      'Carrier',
      'NAIC Number'
    );

    const policy = this.getInsuranceReviewValue(
      insuranceReviewCoverageKey,
      'Policy',
      'Policy Number'
    );
    const coverageEffectiveDate = this.getInsuranceReviewValue(
      insuranceReviewCoverageKey,
      'Policy',
      'Coverage Effective Date'
    );
    const coverageExpirationDate = this.getInsuranceReviewValue(
      insuranceReviewCoverageKey,
      'Policy',
      'Coverage Expiration Date'
    );

    const insurancePremium = this.getInsuranceReviewValue(
      insuranceReviewCoverageKey,
      'Policy',
      'Total Premium'
    );

    if (insuranceAgentName || insuranceAgentEmail || insuranceAgentPhone || insuranceAgentAddress) {
      selectedQuote.agent = {
        ...selectedQuote.agent,
        ...(insuranceAgentName && { name: insuranceAgentName }),
        ...(insuranceAgentEmail && { email: insuranceAgentEmail }),
        ...(insuranceAgentPhone && { phone: insuranceAgentPhone }),
        ...(insuranceAgentAddress && { address: insuranceAgentAddress }),
      };
    }

    if (carrier) selectedQuote.carrier = carrier;
    if (naic) selectedQuote.carrierNaic = naic;

    if (policy) selectedQuote.policyNumber = policy;
    if (coverageEffectiveDate)
      selectedQuote.insuranceEffectiveDate = coverageEffectiveDate;
    if (coverageExpirationDate)
      selectedQuote.insuranceExpiryDate = coverageExpirationDate;
    if (insurancePremium) {
      selectedQuote.insurancePremium = parseFloat(
        insurancePremium.replace(/[^0-9.]/g, '')
      );
      selectedQuote.remainingAmount =
        selectedQuote.insurancePremium - selectedQuote.paidAlready;
    }
  }

  *requestResolutions(includeNotification: boolean) {
    const selectedQuote = this.getSelectedQuote();
    yield this.createResolutions({
      insuranceQuoteDetailId: selectedQuote?.insuranceQuoteDetailId,
      documentIdsForRevision: this.getRevisionDocumentIds(),
      sections: this.insuranceReviewData,
      notify: includeNotification,
      loanId: this.insuranceInformationStore.loanId
    });
  }

  *setRevisionComment(coverage: string, reviewItem: any) {
    if (!this.getInsuranceReviewerComment(coverage, reviewItem?.uniqueName)) {
      let response = yield this.getRevisionText({
        loanId: Number(this.insuranceInformationStore.loanId) || 0,
        quoteId: Number(this.selectedQuote) || 0,
        uniqueName: reviewItem?.uniqueName,
      });
      this.insuranceReviewData[coverage] = this.insuranceReviewData[
        coverage
      ].map(section => ({
        ...section,
        items: section.items.map(item =>
          item.uniqueName === reviewItem.uniqueName
            ? {
              ...item,
              reviewerComment: response?.data?.data?.revisionText,
            }
            : item
        ),
      }));
    }
  }

  *updateAutomatedResolutionForQuote(value, selectedQuote) {
    try {
      const response = yield this.updateAutoRevisionForQuote(
        selectedQuote,
        value
      );
      if (response?.data?.Success) {
        yield this.getInsuranceQuotes(this.insuranceInformationStore.loanId);
        this.setSelectedQuote(selectedQuote);
      }
    } catch (e) {
      console.error('Error on updating automated resolutions for quote', e);
    }
  }

  getRevisionItemsForCoverage(coverage: string) {
    return this.insuranceReviewData[coverage]
      .reduce((acc, sections) => acc.concat(sections), [])
      .filter(section => section.items.some(item => item.sendForRevision))
      .map(section =>
        section.items.map(item => ({
          ...item,
          itemRevisionDisplayName: `${section.sectionName} - ${item.itemDisplayName}`,
          reviewerCommentRevision: item.reviewerComment,
        }))
      )
      .reduce((acc, items) => acc.concat(items), [])
      .filter(item => item.sendForRevision);
  }

  *acceptPreview() {
    this.showPreview = false;
  }

  *declinePreview() {
    this.showPreview = false;
  }

  *removeFlagForDoc(key) {
    this.insuranceReviewData[key].map(section =>
      section.items.map(item => {
        item.needsRevisionFlag = false;
        item.sendForRevision = false;
      })
    );
    yield this.saveClearFlagButtonLogInfo();
  }

  get readOnlyForm() {
    return (
      this.insuranceAutomationStatus == InsuranceAutomationStatus.IN_PROGRESS
    );
  }

  get automationStatusMappingValue() {
    switch (this.insuranceAutomationStatus) {
      case InsuranceAutomationStatus.IN_PROGRESS:
        return 'In Progress';
      case InsuranceAutomationStatus.SUCCESS:
        return 'Automated';
      case InsuranceAutomationStatus.FAILURE:
        return 'Manual';
    }
  }

  isReadOnlySection(sectionKey: string): boolean {
    if (
      !sectionKey ||
      !this.automatedDocumentsData ||
      Object.keys(this.automatedDocumentsData).length === 0
    )
      return false;

    if (sectionKey === 'Package Coverage' && this.readOnlyPackageSection)
      return true;

    const readOnlyKeys = Object.keys(this.automatedDocumentsData)
      .filter(key => this.automatedDocumentsData[key]?.readOnly)
      .map(key => this.normalizeKey(key));

    return readOnlyKeys.includes(this.normalizeKey(sectionKey));
  }

  normalizeKey(key: string): string {
    if (!key) return '';
    return key
      .replace(/\s*\|\*\|\s*/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }
}
